import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReportTable from "../../common/ReportTable/ReportTable";
import TreeView from "../../common/treeView";
import buildTree from "../../../utils/buildTree";
import Dropdown from "../../common/dropDown";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import EpsfPageBreadcrumb from "../epsf/EpsfPageBreadcrumb";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";

//must imported
import { useUserData } from "../../../hooks/useUserData";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useLoader } from "../../../context/LoaderContext";
import ConfirmModal from "../../common/ConfirmModal";
import InfoAlert from "../../common/InfoAlert";
import useConfirmModal from "../../../hooks/useConfirmModal";
import useInfoAlert from "../../../hooks/useInfoAlert";
import useToast from "../../../hooks/useToast";
import ToastContainer from "../../common/ToastContainer";
import utils from "../../../utils/utils";
import { ACTIONS, getPermissionMessage } from "../../../constants/message";
import { resolveSessionPermissions } from "../../../utils/permissionUtils";
import {
  canAddTemplate,
  canDeleteTemplate,
  canEditTemplate,
  canEditTemplatePlaceholders,
  canSelectTemplateMilestonePlan,
} from "./isfPermissionUtils";
import { fetchRoleLookup, resolveRecipientRoleIds, roleIdToName } from "../../../services/lookupService";
import {
  alignPlaceholdersToFolders,
  buildTemplatePlaceholderPayload,
  dedupeTemplatePlaceholders,
  fetchTemplatePlaceholders,
  filterManualTemplatePlaceholders,
  getAssignedMilestoneIds,
  getPlanMilestoneIdSet,
  getTemplateUsedMilestoneIds,
  inferManualMilestoneAssignment,
  mergeManualTemplatePlaceholders,
  normFolderId,
  resolvePlaceholderFolderId,
  normalizeTemplateFolders,
  placeholderMatchesFolder,
  placeholderSameIdentity,
  removeTemplatePlaceholder as removeTemplatePlaceholderApi,
  saveTemplatePlaceholders,
  stripGuid,
} from "./isfPlaceholderService";
import {
  buildTemplatePlaceholdersFromMilestone,
  fetchMilestonePlanLookup,
  fetchMilestoneLookup,
  fetchTemplateMilestonePlan,
  extractMilestoneApiMessage,
  isMilestoneApiSuccess,
  isMilestonePlanLookupBroken,
  mapMilestoneLookupOptions,
  mapMilestonePlanLookupOptions,
  normalizeMilestoneLookup,
  normalizeMilestonePlanLookup,
  normalizeTemplateMilestonePlan,
  previewMilestonePlanMapping,
  normalizeMilestonePlanMappingPreview,
  resolveMilestonePlanOption,
  summarizeMilestonePlanMappingPreview,
  milestoneCategoryClassName,
} from "./isfMilestoneService";
import TemplateFolderAssignments from "./TemplateFolderAssignments";
import {
  FOLDER_LIBRARY_CATEGORIES,
  fetchFolderLibrary,
  isFolderLibrarySuccess,
  mapFolderLibraryToTemplateFolders,
  normalizeFolderCategory,
  normalizeFolderLibraryList,
} from "./isfFolderLibraryService";
import "./TemplatePlaceholderManager.scss";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import {
  ISF_REPORT_ACTIONS_COLUMN,
  ISF_REPORT_PINNED_COLUMNS,
  matchTextColumnSearch,
} from "./isfReportTable";
import "./isfReportTable.scss";

const TEMPLATE_FIELD_OPTIONS = [
  { key: "TemplateName", label: "Template Name" },
  { key: "TemplateCategory", label: "Category" },
  { key: "MilestonePlanName", label: "Milestone Plan" },
  { key: "CreatedByName", label: "Created By" },
  { key: "FolderCount", label: "Folders" },
  { key: "SystemDateTime", label: "Creation Date Time" },
  { key: ISF_REPORT_ACTIONS_COLUMN, label: "Actions" },
];

const DEFAULT_TEMPLATE_COLUMNS = [
  "TemplateName",
  "TemplateCategory",
  "MilestonePlanName",
  "CreatedByName",
  "SystemDateTime",
  ISF_REPORT_ACTIONS_COLUMN,
];

const TEMPLATE_NAME_MAX_LENGTH = 100;

const TEMPLATE_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const TEMPLATE_DATE_PRESETS = DEFAULT_PRESETS;

const TEMPLATE_FILTERS_INITIAL = {
  createdBy: [],
  createdDateRange: { ...TEMPLATE_EMPTY_DATE_RANGE },
};

const getTemplateCreatedByLabel = (row) =>
  String(row?.CreatedByName ?? row?.createdByName ?? "").trim();

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesTemplateDateRange = (value, range) => {
  if (
    !range ||
    range.preset === "All Time" ||
    (!range.startDate && !range.endDate)
  ) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw.toUpperCase() === "N/A") return false;
  const date = utils.parseDateInput(raw);
  if (!date) return false;
  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

/** Remove a folder row and all descendants from flat template folder list. */
function removeFolderAndDescendants(flat, folderId) {
  if (!Array.isArray(flat) || !folderId) return flat;
  const remove = new Set([String(folderId)]);
  let added = true;
  while (added) {
    added = false;
    for (const row of flat) {
      const pid = row.ParentFolderIdRef;
      if (pid != null && remove.has(String(pid)) && !remove.has(String(row.FolderId))) {
        remove.add(String(row.FolderId));
        added = true;
      }
    }
  }
  return flat.filter((row) => !remove.has(String(row.FolderId)));
}

function categoryOptionFromRow(row, options) {
  const key = normalizeFolderCategory(row?.TemplateCategory);
  return (
    options.find((item) => item.value === key || item.label === key) || {
      value: key,
      label: key,
    }
  );
}

function collectRemovedFolderIds(flat, folderId) {
  const remaining = removeFolderAndDescendants(flat, folderId);
  const remainingIds = new Set(remaining.map((r) => String(r.FolderId)));
  const removed = new Set();
  (flat || []).forEach((row) => {
    if (!remainingIds.has(String(row.FolderId))) {
      removed.add(String(row.FolderId));
    }
  });
  return removed;
}

/** Milestones that block deleting a folder (saved assignments + plan-mapped). */
function collectMilestonesBlockingFolderDelete(
  folderId,
  flatTreeData,
  templatePlaceholders,
  planMappingPreview,
) {
  const removedFolderIds = collectRemovedFolderIds(flatTreeData, folderId);
  const foldersInScope = (flatTreeData || []).filter((row) =>
    removedFolderIds.has(String(row.FolderId)),
  );
  const blocked = [];
  const seen = new Set();

  const addBlocked = (key, code, folderName) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    blocked.push({
      code: code || "Milestone",
      folderName: folderName || "",
    });
  };

  foldersInScope.forEach((folderRow) => {
    const folderName = folderRow.FolderName || "";

    (templatePlaceholders || []).forEach((p) => {
      if (!stripGuid(p.MilestoneId)) return;
      if (!placeholderMatchesFolder(p, folderRow.FolderId, folderName)) return;
      addBlocked(
        `saved:${stripGuid(p.MilestoneId)?.toLowerCase()}:${normFolderId(folderRow.FolderId)}`,
        p.MilestoneCode || p.MilestoneName || p.MilestoneNameRef || p.PlaceholderName,
        folderName,
      );
    });

    (planMappingPreview || []).forEach((row) => {
      if (row.FolderAction === "MissingDefaultFolder") return;
      if (folderNameKey(row.DefaultFolderName) !== folderNameKey(folderName)) return;
      addBlocked(
        `plan:${stripGuid(row.MilestoneId)?.toLowerCase() || row.MilestoneCode}`,
        row.MilestoneCode || row.MilestoneName,
        folderName,
      );
    });
  });

  return blocked;
}

function placeholdersToTreeNodes(placeholders) {
  return (placeholders || []).map((p, i) => ({
    id: p._localKey || (p.PlaceholderId ? `tpl-ph-${p.PlaceholderId}` : `tpl-ph-temp-${i}`),
    name: p.PlaceholderName || "Expected document",
    type: "placeholder",
    parentId: p.TemplateFolderId,
    placeholderMeta: p,
    isRequired: p.IsRequired !== false && p.IsRequired !== 0,
    RequiresSignature:
      p.RequiresSignature === true ||
      p.RequiresSignature === 1 ||
      p.RequiresSignature === "1",
    isTemplatePlaceholder: true,
  }));
}

function buildTreeWithPlaceholders(folders, placeholders) {
  const normalizedFolders = normalizeTemplateFolders(folders);
  const folderNodes = normalizedFolders.map((item) => ({
    id: item.FolderId,
    name: item.FolderName,
    type: "folder",
    parentId: item.ParentFolderIdRef,
  }));
  return buildTree([...folderNodes, ...placeholdersToTreeNodes(placeholders)]);
}

function folderNameKey(name) {
  return String(name || "").trim().toLowerCase();
}

function ensurePlanFoldersInTree(folders, planPreview) {
  const normalized = normalizeTemplateFolders(folders || []);
  const byName = new Map();
  normalized.forEach((f) => {
    const key = folderNameKey(f.FolderName);
    if (key) byName.set(key, f);
  });

  const next = [...normalized];
  const addedIds = [];

  (planPreview || []).forEach((row) => {
    const folderName = String(row.DefaultFolderName || "").trim();
    if (!folderName || row.FolderAction === "MissingDefaultFolder") return;

    const key = folderNameKey(folderName);
    if (byName.has(key)) return;

    const newId = utils.uuidv4();
    const newFolder = {
      FolderId: newId,
      FolderName: folderName,
      ParentFolderIdRef: null,
      _planAdded: true,
    };
    next.push(newFolder);
    byName.set(key, newFolder);
    addedIds.push(String(newId));
  });

  return { folders: next, addedIds };
}

function planMilestoneTreeNodes(planPreview, folders) {
  const folderByName = new Map();
  normalizeTemplateFolders(folders).forEach((f) => {
    folderByName.set(folderNameKey(f.FolderName), f.FolderId);
  });

  return (planPreview || [])
    .filter((row) => row.FolderAction !== "MissingDefaultFolder" && row.DefaultFolderName)
    .map((row) => {
      const folderId = folderByName.get(folderNameKey(row.DefaultFolderName));
      if (!folderId) return null;
      return {
        id: `plan-ms-${row.MilestoneCode}-${row.SortOrder}`,
        name: `${row.MilestoneCode} — ${row.MilestoneName}`,
        type: "milestone",
        parentId: folderId,
        milestoneMeta: row,
        RequiresSignature:
          row.RequiresSignature === true ||
          row.RequiresSignature === 1 ||
          row.RequiresSignature === "1",
        isPlanMilestone: true,
      };
    })
    .filter(Boolean);
}

function assignedMilestoneTreeNodes(placeholders, folders, planPreview = []) {
  const normalizedFolders = normalizeTemplateFolders(folders);
  const planByMilestoneId = new Map();
  (planPreview || []).forEach((row) => {
    const id = stripGuid(row.MilestoneId)?.toLowerCase();
    if (id) planByMilestoneId.set(id, row);
  });

  const grouped = new Map();
  (placeholders || []).forEach((p) => {
    const milestoneId = stripGuid(p.MilestoneId);
    if (!milestoneId) return;
    const folderId = resolvePlaceholderFolderId(p, normalizedFolders);
    if (!folderId) return;

    const key = milestoneId.toLowerCase();
    const existing = grouped.get(key);
    const isManual = Boolean(
      p._manualMilestone ||
        inferManualMilestoneAssignment(p, planPreview, normalizedFolders),
    );
    if (existing && !isManual) return;
    if (existing && isManual) {
      grouped.delete(key);
    } else if (existing) {
      return;
    }

    const planRow = planByMilestoneId.get(key);
    grouped.set(key, {
      milestoneId,
      code:
        p.MilestoneCode ||
        p.MilestoneCodeRef ||
        planRow?.MilestoneCode ||
        "Milestone",
      name:
        p.MilestoneName ||
        p.MilestoneNameRef ||
        planRow?.MilestoneName ||
        p.PlaceholderName,
      folderId,
      isManualMilestone: isManual,
      RequiresSignature:
        isTruthyFlag(p?.RequiresSignature) || isTruthyFlag(planRow?.RequiresSignature),
    });
  });

  return Array.from(grouped.values()).map((row) => ({
    id: `assigned-ms-${row.milestoneId}`,
    name: `${row.code} — ${row.name}`,
    type: "milestone",
    parentId: row.folderId,
    milestoneMeta: row,
    RequiresSignature: row.RequiresSignature,
    isAssignedMilestone: true,
    isManualMilestone: row.isManualMilestone,
  }));
}

function buildTemplateTree(folders, placeholders, planPreview, showPlanMilestones, planMilestoneIds) {
  const normalizedFolders = normalizeTemplateFolders(folders);
  const folderNodes = normalizedFolders.map((item) => ({
    id: item.FolderId,
    name: item.FolderName,
    type: "folder",
    parentId: item.ParentFolderIdRef,
  }));
  const milestoneNodes = showPlanMilestones
    ? planMilestoneTreeNodes(planPreview, normalizedFolders)
    : assignedMilestoneTreeNodes(placeholders, normalizedFolders, planPreview);
  return buildTree([
    ...folderNodes,
    ...milestoneNodes,
    ...placeholdersToTreeNodes(
      (placeholders || []).filter((p) => !p.MilestoneId),
    ),
  ]);
}

function milestonePreviewKey(row) {
  return `plan-ms-${row.MilestoneCode}-${row.SortOrder}`;
}

function buildMilestoneLookupMap(lookupRows = []) {
  const map = new Map();
  (lookupRows || []).forEach((row) => {
    const id = stripGuid(row?.MilestoneId ?? row?.milestoneId)?.toLowerCase();
    if (id) map.set(id, row);
  });
  return map;
}

function resolveMilestoneCategory(milestoneId, planRow, milestoneLookupMap) {
  if (planRow?.Category) return String(planRow.Category).trim();
  const id = stripGuid(milestoneId)?.toLowerCase();
  const hit = id ? milestoneLookupMap?.get(id) : null;
  return String(hit?.Category ?? hit?.category ?? "").trim();
}

function buildAssignedMilestoneRow(
  milestoneId,
  placeholders,
  planRows,
  milestoneLookupMap,
  folders = [],
) {
  const id = stripGuid(milestoneId)?.toLowerCase();
  if (!id) return null;

  const milestonePlaceholders = (placeholders || []).filter(
    (p) => stripGuid(p.MilestoneId)?.toLowerCase() === id,
  );
  const planRow = (planRows || []).find(
    (row) => stripGuid(row.MilestoneId)?.toLowerCase() === id,
  );
  const first = milestonePlaceholders[0];
  if (!first && !planRow) return null;

  const normalizedFolders = normalizeTemplateFolders(folders);
  const isAdHoc =
    !planRow ||
    milestonePlaceholders.some((p) =>
      inferManualMilestoneAssignment(p, planRows, normalizedFolders),
    );

  const requiresSignature =
    isTruthyFlag(first?.RequiresSignature) || isTruthyFlag(planRow?.RequiresSignature);
  const category = resolveMilestoneCategory(milestoneId, planRow, milestoneLookupMap);

  return {
    MilestoneId: stripGuid(milestoneId),
    MilestoneCode: first?.MilestoneCode || planRow?.MilestoneCode || "Milestone",
    MilestoneName:
      first?.MilestoneName ||
      first?.MilestoneNameRef ||
      planRow?.MilestoneName ||
      first?.PlaceholderName ||
      "",
    Description: first?.Description || planRow?.Description || "",
    Category: category,
    DefaultFolderName: planRow?.DefaultFolderName || first?.FolderName || "",
    ExpectedTiming: planRow?.ExpectedTiming || "",
    PlaceholderCount: milestonePlaceholders.length || planRow?.PlaceholderCount || 1,
    FolderAction: planRow?.FolderAction || "Existing",
    RequiresSignature: requiresSignature,
    DefaultUploaderRoles: first?.DefaultAssigneeRole || planRow?.DefaultUploaderRoles || "",
    DefaultSignerRoles: first?.SignerRoles || planRow?.DefaultSignerRoles || "",
    TriggeringDocuments: milestonePlaceholders.map((p) => p.PlaceholderName).join("|"),
    SortOrder: planRow?.SortOrder ?? 0,
    _assignedMilestone: isAdHoc,
  };
}

function resolveSelectedMilestone(
  selectedMilestoneKey,
  planRows,
  placeholders,
  milestoneLookupMap,
  folders,
) {
  const fromPlan = planRows.find((row) => milestonePreviewKey(row) === selectedMilestoneKey);
  if (fromPlan) {
    return {
      ...fromPlan,
      Category: resolveMilestoneCategory(fromPlan.MilestoneId, fromPlan, milestoneLookupMap),
      _assignedMilestone: false,
    };
  }

  if (!selectedMilestoneKey?.startsWith("assigned-ms-")) return null;

  const milestoneId = selectedMilestoneKey.replace("assigned-ms-", "");
  const built = buildAssignedMilestoneRow(
    milestoneId,
    placeholders,
    planRows,
    milestoneLookupMap,
    folders,
  );
  if (!built) return null;

  if (!built._assignedMilestone) {
    const planRow = planRows.find(
      (row) => stripGuid(row.MilestoneId)?.toLowerCase() === stripGuid(milestoneId)?.toLowerCase(),
    );
    if (planRow) {
      return {
        ...planRow,
        Category: resolveMilestoneCategory(milestoneId, planRow, milestoneLookupMap),
        _assignedMilestone: false,
      };
    }
  }

  return built;
}

function assignedMilestoneKey(milestoneId) {
  return `assigned-ms-${stripGuid(milestoneId)}`;
}

function placeholderRequiresSignature(placeholder, planRows = []) {
  if (isTruthyFlag(placeholder?.RequiresSignature)) return true;
  const milestoneId = stripGuid(placeholder?.MilestoneId)?.toLowerCase();
  if (!milestoneId) return false;
  const planRow = (planRows || []).find(
    (row) => stripGuid(row.MilestoneId)?.toLowerCase() === milestoneId,
  );
  return isTruthyFlag(planRow?.RequiresSignature);
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === "1";
}

function formatRoleLabels(storedValue, roleOptions = []) {
  const ids = resolveRecipientRoleIds(storedValue, roleOptions);
  if (!ids.length) return "—";
  return ids.map((id) => roleIdToName(id, roleOptions)).filter(Boolean).join(", ");
}

function parseTriggeringDocuments(row) {
  const raw = row?.TriggeringDocuments || row?.triggeringDocuments || "";
  if (!raw) {
    const count = Number(row?.PlaceholderCount ?? 1);
    if (count === 1 && row?.MilestoneName) {
      return [row.MilestoneName];
    }
    return [];
  }
  return String(raw)
    .split("|")
    .map((name) => name.trim())
    .filter(Boolean);
}

function folderActionDescription(action) {
  switch (action) {
    case "Existing":
      return "This folder already exists in the template.";
    case "WillCreate":
      return "This folder will be created when you save the template.";
    case "Created":
      return "This folder was created from the milestone plan.";
    case "MissingDefaultFolder":
      return "No default folder is set on this milestone — it will be skipped.";
    default:
      return "";
  }
}

function MilestoneWorkflowSummary({ row, roleOptions }) {
  return (
    <div className="tpl-detail__workflow">
      <div className="tpl-detail__workflow-row">
        <span className="tpl-detail__workflow-label">Upload</span>
        <span>{formatRoleLabels(row.DefaultUploaderRoles, roleOptions)}</span>
      </div>
      {isTruthyFlag(row.RequiresSignature) ? (
        <div className="tpl-detail__workflow-row">
          <span className="tpl-detail__workflow-label">Sign</span>
          <span>{formatRoleLabels(row.DefaultSignerRoles, roleOptions)}</span>
        </div>
      ) : null}
      <div className="tpl-detail__badges mt-2">
        {isTruthyFlag(row.RequiresSignature) ? (
          <span className="tpl-plan-map__badge tpl-plan-map__badge--sig">Signature required</span>
        ) : (
          <span className="tpl-plan-map__badge tpl-plan-map__badge--existing">No signature</span>
        )}
        <span className="tpl-plan-map__badge tpl-plan-map__badge--required">Upload required</span>
      </div>
    </div>
  );
}

function ExpectedDocumentsList({ documents, placeholders = [], planRows = [] }) {
  if (placeholders.length > 0) {
    return (
      <ul className="tpl-detail__doc-list">
        {placeholders.map((p) => (
          <li key={p._localKey || p.PlaceholderId}>
            <i className="far fa-file-lines" />
            <span>{p.PlaceholderName}</span>
            {placeholderRequiresSignature(p, planRows) ? (
              <span className="tpl-plan-map__badge tpl-plan-map__badge--sig ms-1">Signature</span>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  if (!documents.length) {
    return <p className="text-muted small mb-0">No triggering documents defined.</p>;
  }

  return (
    <ul className="tpl-detail__doc-list">
      {documents.map((name) => (
        <li key={name}>
          <i className="far fa-file-lines" />
          <span>{name}</span>
        </li>
      ))}
    </ul>
  );
}

const FOLDER_ACTION_META = {
  Existing: { label: "Existing", className: "tpl-plan-map__badge tpl-plan-map__badge--existing" },
  WillCreate: { label: "Will create", className: "tpl-plan-map__badge tpl-plan-map__badge--create" },
  Created: { label: "Created", className: "tpl-plan-map__badge tpl-plan-map__badge--created" },
  MissingDefaultFolder: {
    label: "No default folder",
    className: "tpl-plan-map__badge tpl-plan-map__badge--missing",
  },
};

function folderActionBadge(action) {
  const meta = FOLDER_ACTION_META[action] || {
    label: action || "Unknown",
    className: "tpl-plan-map__badge",
  };
  return <span className={meta.className}>{meta.label}</span>;
}

function MilestoneBasicRow({ row, isAdHoc = false, canRemove = false, onRemove }) {
  const docs = parseTriggeringDocuments(row);
  const docCount = row.PlaceholderCount ?? docs.length ?? 1;

  return (
    <div className="tpl-detail__milestone-basic">
      {isAdHoc && canRemove && onRemove ? (
        <button
          type="button"
          className="tpl-detail__milestone-remove"
          title="Remove from folder"
          aria-label={`Remove ${row.MilestoneCode || "milestone"} from folder`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(row);
          }}
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
        </button>
      ) : null}
      <div className="tpl-detail__milestone-basic-head">
        <strong>{row.MilestoneCode}</strong>
        <span className="text-muted">{row.MilestoneName}</span>
      </div>
      <div className="tpl-detail__milestone-basic-meta">
        <span className="text-muted">{docCount} doc(s)</span>
        {row.ExpectedTiming ? (
          <span className="text-muted">{row.ExpectedTiming}</span>
        ) : null}
        {row.Category ? (
          <span className={milestoneCategoryClassName(row.Category)}>{row.Category}</span>
        ) : null}
        {isAdHoc ? (
          <span className="tpl-plan-map__badge tpl-plan-map__badge--adhoc">Ad-hoc</span>
        ) : null}
        {isTruthyFlag(row.RequiresSignature) ? (
          <span className="tpl-plan-map__badge tpl-plan-map__badge--sig">Signature</span>
        ) : null}
      </div>
    </div>
  );
}

function MilestoneDetailHead({ milestone, canRemoveAdHoc = false, onRemoveAdHoc }) {
  const description = String(milestone?.Description || "").trim();

  return (
    <div className="tpl-detail__head tpl-detail__head--with-actions">
      {canRemoveAdHoc && onRemoveAdHoc ? (
        <button
          type="button"
          className="tpl-detail__milestone-remove"
          title="Remove from folder"
          aria-label={`Remove ${milestone.MilestoneCode || "milestone"} from folder`}
          onClick={() => onRemoveAdHoc(milestone)}
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
        </button>
      ) : null}
      <span className="tpl-detail__eyebrow">Milestone</span>
      <div className="tpl-detail__title-row">
        {description ? (
          <span
            className="tpl-detail__desc-hint"
            title={description}
            aria-label={`Description: ${description}`}
          >
            <i className="far fa-eye" aria-hidden="true" />
          </span>
        ) : null}
        <h6 className="tpl-detail__title mb-0">{milestone.MilestoneCode}</h6>
      </div>
      <p className="tpl-detail__subtitle mb-0">{milestone.MilestoneName}</p>
    </div>
  );
}

function TemplateSelectionDetail({
  selectedFolderId,
  selectedMilestoneKey,
  flatTreeData,
  planMappingPreview,
  templatePlaceholders,
  selectedMilestonePlan,
  planMappingPreviewLoading,
  roleOptions = [],
  milestoneOptions = [],
  milestoneLookupMap,
  canEditPlaceholders,
  templateId,
  userData,
  onAssignMilestone,
  onRemoveAdHocMilestone,
  milestoneLibraryLoading = false,
}) {
  const folderRow = flatTreeData.find(
    (f) => normFolderId(f.FolderId) === normFolderId(selectedFolderId),
  );
  const folderName = folderRow?.FolderName || "";
  const folderKey = folderNameKey(folderName);
  const planRows = planMappingPreview || [];
  const planTotal = planRows.length;

  const milestonesInFolder = planRows.filter(
    (row) => folderNameKey(row.DefaultFolderName) === folderKey,
  );

  const selectedMilestone = resolveSelectedMilestone(
    selectedMilestoneKey,
    planRows,
    templatePlaceholders,
    milestoneLookupMap,
    flatTreeData,
  );

  const folderPlaceholders = (templatePlaceholders || []).filter((p) =>
    placeholderMatchesFolder(p, selectedFolderId, folderName),
  );

  const folderDocTotal = milestonesInFolder.reduce(
    (sum, row) => sum + Number(row.PlaceholderCount || 0),
    0,
  );

  if (planMappingPreviewLoading) {
    return <p className="text-muted mb-0">Loading plan details…</p>;
  }

  if (selectedMilestone) {
    const documents = parseTriggeringDocuments(selectedMilestone);
    const milestonePlaceholders = folderPlaceholders.filter(
      (p) =>
        !selectedMilestone.MilestoneId ||
        String(p.MilestoneId || "").toLowerCase() ===
          String(selectedMilestone.MilestoneId || "").toLowerCase(),
    );
    const planIndex = planRows.findIndex(
      (row) =>
        milestonePreviewKey(row) === selectedMilestoneKey ||
        (selectedMilestone?.MilestoneId &&
          stripGuid(row.MilestoneId)?.toLowerCase() ===
            stripGuid(selectedMilestone.MilestoneId)?.toLowerCase()),
    );

    const canRemoveAdHoc =
      Boolean(selectedMilestone._assignedMilestone) &&
      canEditPlaceholders &&
      Boolean(templateId);

    return (
      <div className="tpl-detail">
        <MilestoneDetailHead
          milestone={selectedMilestone}
          canRemoveAdHoc={canRemoveAdHoc}
          onRemoveAdHoc={
            canRemoveAdHoc && onRemoveAdHocMilestone
              ? () => onRemoveAdHocMilestone(selectedMilestone, selectedFolderId)
              : undefined
          }
        />

        <div className="tpl-detail__stats">
          {planIndex >= 0 ? (
            <span className="tpl-detail__stat">
              Plan order {planIndex + 1} of {planTotal}
            </span>
          ) : null}
          {selectedMilestone._assignedMilestone ? (
            <span className="tpl-plan-map__badge tpl-plan-map__badge--adhoc">Ad-hoc</span>
          ) : null}
          {selectedMilestone.Category ? (
            <span className={milestoneCategoryClassName(selectedMilestone.Category)}>
              {selectedMilestone.Category}
            </span>
          ) : null}
        </div>

        <dl className="tpl-detail__meta">
          <div>
            <dt>Default folder</dt>
            <dd>{selectedMilestone.DefaultFolderName || "—"}</dd>
          </div>
          <div>
            <dt>Expected timing</dt>
            <dd>{selectedMilestone.ExpectedTiming || "—"}</dd>
          </div>
          <div>
            <dt>Expected documents</dt>
            <dd>{selectedMilestone.PlaceholderCount ?? documents.length ?? 1}</dd>
          </div>
          <div>
            <dt>Folder on save</dt>
            <dd>{folderActionBadge(selectedMilestone.FolderAction)}</dd>
          </div>
        </dl>

        {folderActionDescription(selectedMilestone.FolderAction) ? (
          <p className="tpl-detail__hint mb-3">{folderActionDescription(selectedMilestone.FolderAction)}</p>
        ) : null}

        <div className="tpl-detail__section">
          <h6 className="tpl-detail__section-title">Workflow</h6>
          <MilestoneWorkflowSummary row={selectedMilestone} roleOptions={roleOptions} />
        </div>

        <div className="tpl-detail__section">
          <h6 className="tpl-detail__section-title">Expected documents</h6>
          <ExpectedDocumentsList
            documents={documents}
            placeholders={milestonePlaceholders}
            planRows={planRows}
          />
          {!milestonePlaceholders.length && !documents.length ? (
            <p className="text-muted small mt-2 mb-0">
              Documents are created when the milestone is assigned to a folder.
            </p>
          ) : null}
        </div>

        {selectedMilestone.FolderAction === "MissingDefaultFolder" ? (
          <p className="tpl-detail__warn mb-0">
            Set a default folder in the Milestone Library before this milestone can be applied.
          </p>
        ) : null}
      </div>
    );
  }

  if (selectedFolderId && folderRow) {
    const planMilestoneIdsInFolder = new Set(
      milestonesInFolder
        .map((row) => stripGuid(row.MilestoneId)?.toLowerCase())
        .filter(Boolean),
    );
    const assignedMilestonesInFolder = [];
    const seenAssigned = new Set(planMilestoneIdsInFolder);
    folderPlaceholders
      .filter((p) => stripGuid(p.MilestoneId))
      .forEach((p) => {
        const id = stripGuid(p.MilestoneId)?.toLowerCase();
        if (!id || seenAssigned.has(id)) return;
        seenAssigned.add(id);
        const row = buildAssignedMilestoneRow(
          p.MilestoneId,
          templatePlaceholders,
          planRows,
          milestoneLookupMap,
          flatTreeData,
        );
        if (row?._assignedMilestone) assignedMilestonesInFolder.push(row);
      });

    const folderMilestoneSummaries = [
      ...milestonesInFolder.map((row) => ({ row, isAdHoc: false })),
      ...assignedMilestonesInFolder.map((row) => ({ row, isAdHoc: true })),
    ];

    return (
      <div className="tpl-detail">
        <div className="tpl-detail__head">
          <span className="tpl-detail__eyebrow">Folder</span>
          <h6 className="tpl-detail__title">{folderName}</h6>
        </div>

        <div className="tpl-detail__stats">
          {selectedMilestonePlan?.planName ? (
            <span className="tpl-detail__stat">Plan: {selectedMilestonePlan.planName}</span>
          ) : null}
          {folderRow._planAdded ? (
            <span className="tpl-detail__stat tpl-detail__stat--create">From plan</span>
          ) : (
            <span className="tpl-detail__stat">Default library</span>
          )}
          {folderMilestoneSummaries.length > 0 ? (
            <span className="tpl-detail__stat">
              {folderMilestoneSummaries.length} milestone(s) · {folderDocTotal} document(s)
            </span>
          ) : null}
        </div>

        {folderMilestoneSummaries.length > 0 ? (
          <div className="tpl-detail__section">
            <h6 className="tpl-detail__section-title">Milestones</h6>
            <p className="tpl-detail__hint mb-2">
              Select a milestone in the folder tree for full details.
            </p>
            <div className="tpl-detail__milestone-list">
              {folderMilestoneSummaries.map(({ row, isAdHoc }) => (
                <MilestoneBasicRow
                  key={
                    isAdHoc
                      ? assignedMilestoneKey(row.MilestoneId)
                      : milestonePreviewKey(row)
                  }
                  row={row}
                  isAdHoc={isAdHoc}
                  canRemove={isAdHoc && canEditPlaceholders && Boolean(templateId)}
                  onRemove={
                    isAdHoc && onRemoveAdHocMilestone
                      ? (milestoneRow) =>
                          onRemoveAdHocMilestone(milestoneRow, selectedFolderId)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        ) : selectedMilestonePlan?.value ? (
          <p className="text-muted small mb-2">No milestones mapped to this folder.</p>
        ) : null}

        <TemplateFolderAssignments
          selectedFolderId={selectedFolderId}
          templateId={templateId}
          templatePlaceholders={templatePlaceholders}
          milestoneOptions={milestoneOptions}
          milestoneLibraryLoading={milestoneLibraryLoading}
          canEdit={canEditPlaceholders}
          userData={userData}
          onAssignMilestone={onAssignMilestone}
        />
      </div>
    );
  }

  return (
    <div className="tpl-detail tpl-detail--empty">
      <p className="text-muted small mb-0">
        Select a folder in the tree to view details and assign milestones.
      </p>
    </div>
  );
}

function snapshotFolders(folders) {
  return JSON.stringify(
    normalizeTemplateFolders(folders || []).map((f) => ({
      FolderId: String(f.FolderId),
      FolderName: f.FolderName || "",
      ParentFolderIdRef: f.ParentFolderIdRef ?? "",
    })),
  );
}

function snapshotPlaceholderPayload(rows) {
  return JSON.stringify(
    (rows || [])
      .map((r, i) => ({
        PlaceholderId: r.PlaceholderId || "",
        TemplateFolderId: String(r.TemplateFolderId || ""),
        PlaceholderName: (r.PlaceholderName || "").trim(),
        Description: r.Description || "",
        IsRequired: r.IsRequired !== false && r.IsRequired !== 0,
        SortOrder: r.SortOrder ?? i,
        DefaultDueDays:
          r.DefaultDueDays === "" || r.DefaultDueDays == null
            ? null
            : Number(r.DefaultDueDays),
        MilestoneId: stripGuid(r.MilestoneId) || "",
        _manualMilestone: Boolean(r._manualMilestone),
      }))
      .sort((a, b) =>
        `${a.TemplateFolderId}-${a.PlaceholderName}`.localeCompare(
          `${b.TemplateFolderId}-${b.PlaceholderName}`,
        ),
      ),
  );
}

function getRemovedManualPlaceholderIds(
  baselinePayload,
  currentRows,
  planMilestoneIds,
  planMappingPreview,
  folders,
) {
  if (!baselinePayload) return [];
  let baseline = [];
  try {
    baseline = JSON.parse(baselinePayload);
  } catch {
    return [];
  }
  const currentManual = filterManualTemplatePlaceholders(
    currentRows,
    planMilestoneIds,
    planMappingPreview,
    folders,
  );
  const currentIds = new Set(
    currentManual.map((p) => String(p.PlaceholderId || "").toLowerCase()).filter(Boolean),
  );
  return baseline
    .filter((row) => {
      if (!row.MilestoneId) return true;
      return (
        row._manualMilestone ||
        inferManualMilestoneAssignment(row, planMappingPreview, folders)
      );
    })
    .map((row) => row.PlaceholderId)
    .filter((id) => id && !currentIds.has(String(id).toLowerCase()));
}

const ISFTemplate = ({ lockedCategory = null } = {}) => {
  //must included
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { modalState, showConfirm, handleConfirm, handleCancel, hideConfirm } =
    useConfirmModal();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { toasts, showToast, removeToast } = useToast();
  const lock = lockedCategory
    ? normalizeFolderCategory(lockedCategory)
    : null;
  //page level
  const [flatTreeData, setFlatTreeData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState(TEMPLATE_FILTERS_INITIAL);
  const [templateCategory] = useState(
    lock
      ? FOLDER_LIBRARY_CATEGORIES.filter((c) => c.value === lock)
      : FOLDER_LIBRARY_CATEGORIES,
  );
  const [isTemplatePopupShow, setTemplatePopupShow] = useState(false);
  const [isFolderAddPopupShow, setFolderAddPopupShow] = useState(false);
  const [treeNode, setTreeNode] = useState(null);
  const [templatePlaceholders, setTemplatePlaceholders] = useState([]);
  const [placeholdersTouched, setPlaceholdersTouched] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [selectedPlaceholderKey, setSelectedPlaceholderKey] = useState(null);
  const [milestonePlanOptions, setMilestonePlanOptions] = useState([]);
  const [milestonePlanLoadError, setMilestonePlanLoadError] = useState("");
  const [selectedMilestonePlan, setSelectedMilestonePlan] = useState(null);
  const [milestonePlanTouched, setMilestonePlanTouched] = useState(false);
  const [planMappingPreview, setPlanMappingPreview] = useState([]);
  const [planMappingPreviewLoading, setPlanMappingPreviewLoading] = useState(false);
  const [selectedMilestoneKey, setSelectedMilestoneKey] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [milestoneLibraryOptions, setMilestoneLibraryOptions] = useState([]);
  const [milestoneLibraryLookup, setMilestoneLibraryLookup] = useState([]);
  const [milestoneLibraryLoading, setMilestoneLibraryLoading] = useState(false);
  const modalBaselineRef = useRef(null);
  const modalInitSeqRef = useRef(0);
  const [folderModalMode, setFolderModalMode] = useState(null);
  const [formData, setFormData] = useState({
    TemplateId: "",
    TemplateName: "",
    OrganizationId: "",
    CreatedBy: "",
    ActionType: "",
    TemplateCategory:
      templateCategory.find((r) => r.value === "eISF") ||
      templateCategory[0] ||
      null,
    Folders: [],
    FolderName: "",
  });

  const { isSystemAdmin: isTemplateSystemAdmin, permissions: templatePermissions } =
    resolveSessionPermissions(userData);

  const canEditPlaceholders =
    isTemplateSystemAdmin || canEditTemplatePlaceholders(userData);

  const canSelectMilestonePlan =
    isTemplateSystemAdmin || canSelectTemplateMilestonePlan(userData);

  const fetchMilestoneLibraryOptions = async () => {
    if (!userData?.OrganizationId) return { options: [], lookup: [] };
    setMilestoneLibraryLoading(true);
    try {
      const response = await fetchMilestoneLookup({ userData });
      if (!isMilestoneApiSuccess(response)) return { options: [], lookup: [] };
      const lookup = normalizeMilestoneLookup(response);
      return { options: mapMilestoneLookupOptions(lookup), lookup };
    } catch {
      return { options: [], lookup: [] };
    } finally {
      setMilestoneLibraryLoading(false);
    }
  };

  const milestoneLookupMap = useMemo(
    () => buildMilestoneLookupMap(milestoneLibraryLookup),
    [milestoneLibraryLookup],
  );

  const fetchMilestonePlanOptions = async () => {
    if (!userData?.OrganizationId) return { options: [], error: "" };
    try {
      const response = await fetchMilestonePlanLookup({ userData });
      if (!isMilestoneApiSuccess(response)) {
        return {
          options: [],
          error: extractMilestoneApiMessage(
            response,
            "Could not load milestone plans.",
          ),
        };
      }
      if (isMilestonePlanLookupBroken(response)) {
        return {
          options: [],
          error:
            "Milestone plan lookup is unavailable. Please ask your administrator to apply the latest eISF milestone database scripts.",
        };
      }
      return {
        options: mapMilestonePlanLookupOptions(normalizeMilestonePlanLookup(response)),
        error: "",
      };
    } catch {
      return { options: [], error: "Could not load milestone plans." };
    }
  };

  const loadMilestonePlanOptions = async () => {
    const { options, error } = await fetchMilestonePlanOptions();
    setMilestonePlanOptions(options);
    setMilestonePlanLoadError(error);
    return options;
  };

  const loadTemplateMilestonePlan = async (templateId, planOptions = milestonePlanOptions) => {
    if (!templateId || !userData?.OrganizationId) {
      setSelectedMilestonePlan(null);
      return null;
    }
    try {
      const response = await fetchTemplateMilestonePlan({ userData, templateId });
      if (isMilestoneApiSuccess(response)) {
        const row = normalizeTemplateMilestonePlan(response);
        const planOption = resolveMilestonePlanOption(
          row?.MilestonePlanId,
          row?.PlanName,
          planOptions,
        );
        setSelectedMilestonePlan(planOption);
        setMilestonePlanTouched(false);
        return planOption;
      }
    } catch {
      setSelectedMilestonePlan(null);
    }
    setMilestonePlanTouched(false);
    return null;
  };

  useEffect(() => {
    if (!isTemplatePopupShow || !userData?.OrganizationId) return;
    fetchRoleLookup(userData)
      .then((roles) => setRoleOptions(Array.isArray(roles) ? roles : []))
      .catch(() => setRoleOptions([]));
  }, [isTemplatePopupShow, userData?.OrganizationId]);

  useEffect(() => {
    if (!selectedMilestonePlan?.value || !milestonePlanOptions.length) return;
    const resolved = resolveMilestonePlanOption(
      selectedMilestonePlan.value,
      selectedMilestonePlan.planName || selectedMilestonePlan.label,
      milestonePlanOptions,
    );
    if (
      resolved &&
      (resolved.label !== selectedMilestonePlan.label ||
        resolved.planName !== selectedMilestonePlan.planName)
    ) {
      setSelectedMilestonePlan(resolved);
    }
  }, [milestonePlanOptions, selectedMilestonePlan?.value]);

  const planMappingStats = useMemo(
    () => summarizeMilestonePlanMappingPreview(planMappingPreview),
    [planMappingPreview],
  );

  const planMilestoneIds = useMemo(
    () => getPlanMilestoneIdSet(planMappingPreview),
    [planMappingPreview],
  );

  useEffect(() => {
    if (!isTemplatePopupShow || !selectedMilestonePlan?.value || !userData?.OrganizationId) {
      setPlanMappingPreview([]);
      setPlanMappingPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setPlanMappingPreviewLoading(true);
      try {
        const response = await previewMilestonePlanMapping({
          userData,
          templateId: formData.TemplateId || null,
          milestonePlanId: selectedMilestonePlan.value,
        });
        if (!cancelled) {
          if (response?.success === 1) {
            setPlanMappingPreview(normalizeMilestonePlanMappingPreview(response));
          } else {
            setPlanMappingPreview([]);
          }
        }
      } catch {
        if (!cancelled) setPlanMappingPreview([]);
      } finally {
        if (!cancelled) setPlanMappingPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isTemplatePopupShow,
    selectedMilestonePlan?.value,
    formData.TemplateId,
    userData?.OrganizationId,
  ]);

  useEffect(() => {
    if (placeholdersTouched || !planMappingPreview.length) return;
    setTemplatePlaceholders((prev) =>
      prev.map((p) => ({
        ...p,
        MilestoneCode: p.MilestoneCode || p.MilestoneCodeRef,
        MilestoneName: p.MilestoneName || p.MilestoneNameRef,
        _manualMilestone: inferManualMilestoneAssignment(p, planMappingPreview, flatTreeData),
      })),
    );
  }, [planMappingPreview, flatTreeData, placeholdersTouched]);

  useEffect(() => {
    if (!planMappingPreview.length || !selectedMilestonePlan?.value) return;
    setFlatTreeData((prev) => {
      const { folders, addedIds } = ensurePlanFoldersInTree(prev, planMappingPreview);
      return addedIds.length || folders.length !== prev.length ? folders : prev;
    });
  }, [planMappingPreview, selectedMilestonePlan?.value]);

  useEffect(() => {
    if (selectedMilestonePlan?.value) return;
    setFlatTreeData((prev) => prev.filter((f) => !f._planAdded));
    setSelectedMilestoneKey(null);
  }, [selectedMilestonePlan?.value]);

  const hasPersistedPlaceholders = useMemo(
    () => (templatePlaceholders || []).some((p) => stripGuid(p.PlaceholderId)),
    [templatePlaceholders],
  );

  const hasMilestoneAssignmentRows = useMemo(
    () => (templatePlaceholders || []).some((p) => stripGuid(p.MilestoneId)),
    [templatePlaceholders],
  );

  const showPlanPreviewInTree = Boolean(
    selectedMilestonePlan?.value &&
      planMappingPreview.length > 0 &&
      !hasPersistedPlaceholders &&
      !hasMilestoneAssignmentRows,
  );

  useEffect(() => {
    setTreeData(
      buildTemplateTree(
        flatTreeData,
        templatePlaceholders,
        planMappingPreview,
        showPlanPreviewInTree,
        planMilestoneIds,
      ),
    );
  }, [
    flatTreeData,
    templatePlaceholders,
    planMappingPreview,
    showPlanPreviewInTree,
    planMilestoneIds,
  ]);

  const milestoneFolderExpandIds = useMemo(() => {
    const ids = new Set();
    (planMappingPreview || []).forEach((row) => {
      const folder = flatTreeData.find(
        (f) => folderNameKey(f.FolderName) === folderNameKey(row.DefaultFolderName),
      );
      if (folder?.FolderId) ids.add(String(folder.FolderId));
    });
    (templatePlaceholders || []).forEach((p) => {
      if (p.MilestoneId && p.TemplateFolderId) {
        ids.add(String(p.TemplateFolderId));
      }
    });
    return Array.from(ids);
  }, [planMappingPreview, flatTreeData, templatePlaceholders]);

  const expandedFolderIds = useMemo(() => {
    const ids = new Set(flatTreeData.map((f) => String(f.FolderId)));
    const resolveFolderId = (rawId) => {
      const match = flatTreeData.find(
        (f) => normFolderId(f.FolderId) === normFolderId(rawId),
      );
      return match?.FolderId ?? rawId;
    };
    if (selectedFolderId) ids.add(String(resolveFolderId(selectedFolderId)));
    milestoneFolderExpandIds.forEach((id) => ids.add(String(id)));
    (templatePlaceholders || []).forEach((p) => {
      if (p.TemplateFolderId) ids.add(String(resolveFolderId(p.TemplateFolderId)));
    });
    return Array.from(ids);
  }, [selectedFolderId, templatePlaceholders, flatTreeData, milestoneFolderExpandIds]);

  useEffect(() => {
    if (!flatTreeData?.length) {
      setSelectedFolderId("");
      return;
    }
    setSelectedFolderId((current) => {
      const stillExists = flatTreeData.some(
        (f) => normFolderId(f.FolderId) === normFolderId(current),
      );
      if (current && stillExists) return current;
      return flatTreeData[0].FolderId;
    });
  }, [flatTreeData]);

  const loadTemplatePlaceholdersForTemplate = async (templateId, folders) => {
    if (!templateId || !userData?.OrganizationId) {
      setTemplatePlaceholders([]);
      setPlaceholdersTouched(false);
      return [];
    }
    const list = await fetchTemplatePlaceholders({
      organizationId: userData.OrganizationId,
      templateId,
      createdBy: userData.StaffId,
    });
    const folderRows = normalizeTemplateFolders(folders ?? flatTreeData);
    const aligned = alignPlaceholdersToFolders(list, folderRows);
    const withKeys = dedupeTemplatePlaceholders(
      aligned.map((p) => ({
        ...p,
        _localKey: p.PlaceholderId ? `tpl-ph-${p.PlaceholderId}` : utils.uuidv4(),
      })),
    );
    setTemplatePlaceholders(withKeys);
    setPlaceholdersTouched(false);
    return withKeys;
  };

  const reloadTemplateFoldersFromServer = async (templateId) => {
    if (!templateId || !userData?.OrganizationId || !userData?.StaffId) return null;

    const response = await api.post(API_ENDPOINTS.ISF.GET_TEMPLATE_BY_ID, {
      TemplateId: templateId,
      TemplateName: "",
      OrganizationId: userData.OrganizationId,
      CreatedBy: userData.StaffId,
      ActionType: "Edit",
      TemplateCategory: null,
      Folders: null,
    });

    if (response.success !== 1) return null;

    const folders = normalizeTemplateFolders(
      Array.isArray(response.data?.StudyFolder) ? response.data.StudyFolder : [],
    );
    setFlatTreeData(folders);
    const loadedPlaceholders = await loadTemplatePlaceholdersForTemplate(templateId, folders);
    return { folders, placeholders: loadedPlaceholders };
  };
  //section 1 Get list

  useEffect(() => {
    if (userData) {
      getAllTemplates();
    }
  }, [userData]);

  const getAllTemplates = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      setListLoading(true);
      const response = await api.post(API_ENDPOINTS.ISF.TEMPLATE_LIST, {
        TemplateId: "",
        TemplateName: "",
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "GetTemplateListData",
        TemplateCategory: null,
        Folders: null,
      });

      if (response.success === 1) {
        //console.log(response);
        setTemplates(response.data || []);
      }
    } catch (error) {
      // handle error if needed
    } finally {
      setListLoading(false);
    }
  };

  const handleRefresh = () => {
    getAllTemplates();
  };
  //section 2 edit

  const openTemplateEditor = async (row) => {
    const initSeq = ++modalInitSeqRef.current;
    resetForm();
    setTemplatePopupShow(true);

    try {
      showLoader(row ? "Loading template…" : "Loading…");

      const [planResult, libraryData] = await Promise.all([
        fetchMilestonePlanOptions(),
        fetchMilestoneLibraryOptions(),
      ]);
      const planOptions = planResult.options || [];

      if (initSeq !== modalInitSeqRef.current) return;

      setMilestonePlanOptions(planOptions);
      setMilestonePlanLoadError(planResult.error || "");
      setMilestoneLibraryOptions(libraryData.options || []);
      setMilestoneLibraryLookup(libraryData.lookup || []);

      if (row) {
        setFormData((prev) => ({
          ...prev,
          TemplateName: row.TemplateName,
          TemplateId: row.TemplateID,
          TemplateCategory: categoryOptionFromRow(row, templateCategory),
        }));

        await Promise.all([
          getTemplatesById(row.TemplateID, initSeq, { silent: true }),
          loadTemplateMilestonePlan(row.TemplateID, planOptions),
        ]);
      } else {
        setSelectedMilestonePlan(null);
        setMilestonePlanTouched(false);
        await getTemplatesDefaultFolders();
      }
    } catch {
      if (initSeq === modalInitSeqRef.current) {
        showAlert({
          title: "Error",
          message: "Could not open the template editor.",
          type: "danger",
        });
      }
    } finally {
      hideLoader();
    }
  };

  const handleEdit = useCallback(
    (row) => {
      const isEditMode = !!row;
      const hasAddPermission = canAddTemplate(userData);
      const hasEditPermission = canEditTemplate(userData);

      if (
        (isEditMode && hasEditPermission) ||
        (!isEditMode && hasAddPermission)
      ) {
        openTemplateEditor(row);
      } else {
        showAlert({
          title: "Warning",
          message: getPermissionMessage(
            isEditMode ? ACTIONS.UPDATE_TEMPLATE : ACTIONS.ADD_TEMPLATE,
          ),
          type: "warning",
        });
      }
    },
    [userData, showAlert],
  );

  const getTemplatesById = async (
    templateId,
    initSeq = modalInitSeqRef.current,
    { silent = false } = {},
  ) => {
    if (!userData?.OrganizationId || !userData?.StaffId) return null;

    try {
      if (!silent && initSeq === modalInitSeqRef.current) {
        showLoader("Loading template details...");
      }
      const response = await api.post(API_ENDPOINTS.ISF.GET_TEMPLATE_BY_ID, {
        TemplateId: templateId,
        TemplateName: "",
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "Edit",
        TemplateCategory: null,
        Folders: null,
      });
      if (initSeq !== modalInitSeqRef.current) return null;

      if (response.success === 1) {
        const folders = normalizeTemplateFolders(
          Array.isArray(response.data?.StudyFolder) ? response.data.StudyFolder : [],
        );
        setFlatTreeData(folders);
        const loadedPlaceholders = await loadTemplatePlaceholdersForTemplate(
          templateId,
          folders,
        );
        if (initSeq !== modalInitSeqRef.current) return null;
        captureModalBaseline({ folders, placeholders: loadedPlaceholders });
        return { folders, placeholders: loadedPlaceholders };
      }
    } catch {
      // handle error if needed
    } finally {
      if (!silent && initSeq === modalInitSeqRef.current) {
        hideLoader();
      }
    }
    return null;
  };

  const getTemplatesDefaultFolders = async (categoryArg) => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    if (formData.TemplateId) return;

    const cat = categoryArg ?? formData.TemplateCategory;
    if (!cat?.value) return;

    try {
      showLoader("Loading folders from library…");
      const response = await fetchFolderLibrary({ userData });
      if (!isFolderLibrarySuccess(response)) {
        showAlert({
          title: "Warning",
          message: "Could not load folder library. Add folders in Folder Library first.",
          type: "warning",
        });
        return;
      }

      const libraryRows = normalizeFolderLibraryList(response);
      const categoryKey = normalizeFolderCategory(cat.value || cat.label);
      const folders = normalizeTemplateFolders(
        mapFolderLibraryToTemplateFolders(libraryRows, categoryKey),
      );

      setFlatTreeData(folders);
      setTemplatePlaceholders([]);
      setPlaceholdersTouched(false);
      setSelectedFolderId("");
      setSelectedPlaceholderKey(null);
      setSelectedMilestoneKey(null);
      captureModalBaseline({
        folders,
        placeholders: [],
        templateName: "",
        templateCategory: cat.label,
      });

      if (!folders.length) {
        showAlert({
          title: "No folders",
          message: `No folders found in the folder library for ${categoryKey}. Add them under Folder Library, then try again.`,
          type: "info",
        });
      }
    } catch {
      showAlert({
        title: "Error",
        message: "Could not load folders from the folder library.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const captureModalBaseline = ({
    templateName = formData.TemplateName,
    templateCategory = formData.TemplateCategory?.label || "",
    folders = flatTreeData,
    placeholders = templatePlaceholders,
    milestonePlanId = selectedMilestonePlan?.value || "",
  } = {}) => {
    modalBaselineRef.current = {
      templateName: (templateName || "").trim(),
      templateCategory: templateCategory || "",
      folders: snapshotFolders(folders),
      placeholders: snapshotPlaceholderPayload(placeholders),
      milestonePlanId: milestonePlanId || "",
    };
  };

  const isTemplateModalDirty = () => {
    if (placeholdersTouched || milestonePlanTouched) return true;

    const baseline = modalBaselineRef.current;
    if (!baseline) {
      return Boolean(
        (formData.TemplateName || "").trim() ||
          (flatTreeData?.length || 0) > 0 ||
          (templatePlaceholders?.length || 0) > 0 ||
          selectedMilestonePlan?.value,
      );
    }

    if ((formData.TemplateName || "").trim() !== baseline.templateName) return true;
    if ((formData.TemplateCategory?.label || "") !== baseline.templateCategory) return true;
    if (snapshotFolders(flatTreeData) !== baseline.folders) return true;
    if (snapshotPlaceholderPayload(templatePlaceholders) !== baseline.placeholders) return true;
    if ((selectedMilestonePlan?.value || "") !== (baseline.milestonePlanId || "")) return true;
    return false;
  };

  const requestCloseTemplateModal = () => {
    if (isTemplateModalDirty()) {
      showConfirm({
        title: "Unsaved changes",
        message: "You have unsaved changes. Close without saving?",
        confirmText: "Close without saving",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-exclamation-triangle text-warning",
        onConfirm: () => {
          hideConfirm();
          handleClose();
        },
        onCancel: () => hideConfirm(),
      });
      return;
    }
    handleClose();
  };

  const handleClose = () => {
    modalBaselineRef.current = null;
    setTemplatePopupShow(false);
    resetForm();
    setTreeNode(null);
    setFolderModalMode(null);
    setTemplatePlaceholders([]);
    setPlaceholdersTouched(false);
    setSelectedFolderId("");
    setSelectedPlaceholderKey(null);
    setSelectedMilestoneKey(null);
    setPlanMappingPreview([]);
    setPlanMappingPreviewLoading(false);
  };

  const handleDelete = useCallback(
    (row) => {
      if (!canDeleteTemplate(userData)) {
        showAlert({
          title: "Warning",
          message: getPermissionMessage(ACTIONS.DELETE_TEMPLATE),
          type: "warning",
        });
        return;
      }

      const templateId = row?.TemplateID || row?.TemplateId;
      if (!templateId) return;

      showConfirm({
        title: "Confirm",
        message: `Do you want to delete "${row?.TemplateName || "this template"}"?`,
        confirmText: "Yes",
        confirmButtonClass: "btn-success",
        icon: "fas fa-plus text-success",
        onConfirm: async () => {
          hideConfirm();
          const authRes = await openAuthModal({
            showDescription: true,
            requireDescription: true,
            introMessage: `Confirm your identity to delete "${row?.TemplateName || "this template"}".`,
            descriptionLabel: "Reason for deleting",
            descriptionPlaceholder: "Enter why you are deleting this template…",
          });
          if (authRes?.Success !== 1) return;
          if (!String(authRes?.Description || "").trim()) return;
          await deleteTemplatesById(templateId);
        },
        onCancel: () => {
          hideConfirm();
        },
      });
    },
    [userData, showConfirm, hideConfirm, showAlert],
  );

  const deleteTemplatesById = async (templateId) => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    try {
      showLoader("deleting template...");
      const response = await api.post(API_ENDPOINTS.ISF.GET_TEMPLATE_BY_ID, {
        TemplateId: templateId,
        TemplateName: "",
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: "Delete",
        TemplateCategory: null,
        Folders: null,
      });
      if (response.success === 1) {
        const restTemplates = templates.filter((item) => {
          return item.TemplateID !== templateId;
        });
        setTemplates(restTemplates);
        showAlert({
          title: "Success",
          message: "Template has been deleted successfully.",
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message:
            response?.message ||
            "This template could not be deleted.",
          type: "danger",
        });
      }
    } catch (error) {
      // handle error if needed
    } finally {
      hideLoader();
    }
  };

  //section 4 form data handle
  const resetForm = () => {
    setFormData({
      TemplateId: "",
      TemplateName: "",
      OrganizationId: "",
      CreatedBy: "",
      ActionType: "",
      TemplateCategory:
        templateCategory.find((r) => r.value === (lock || "eISF")) ||
        templateCategory[0] ||
        null,
      Folders: flatTreeData,
    });
    setFlatTreeData([]);
    setTemplatePlaceholders([]);
    setPlaceholdersTouched(false);
    setSelectedMilestonePlan(null);
    setMilestonePlanTouched(false);
    setMilestonePlanLoadError("");
    setPlanMappingPreview([]);
    setPlanMappingPreviewLoading(false);
    setSelectedFolderId("");
    setSelectedPlaceholderKey(null);
    setSelectedMilestoneKey(null);
  };
  const handleCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      TemplateCategory: category || null,
    }));
    if (category?.value) {
      getTemplatesDefaultFolders(category);
    }
  };

  const handleFormInput = (e) => {
    const { name, value, type, checked, files } = e.target;
    const nextValue =
      type === "checkbox"
        ? checked
        : type === "file"
          ? files[0]
          : name === "TemplateName"
            ? String(value || "").slice(0, TEMPLATE_NAME_MAX_LENGTH)
            : value;
    setFormData({
      ...formData,
      [name]: nextValue,
    });
  };

  //section 5 tree actions (template: root from toolbar; subfolder + rename + delete from menu)
  const handleTreeAction = (node, action) => {
    if (action === "newFolder") {
      setTreeNode(null);
      setFolderModalMode("addRoot");
      setFormData((prev) => ({ ...prev, FolderName: "" }));
      setFolderAddPopupShow(true);
    } else if (action === "subFolder") {
      setTreeNode(node);
      setFolderModalMode("addSub");
      setFormData((prev) => ({ ...prev, FolderName: "" }));
      setFolderAddPopupShow(true);
    } else if (action === "renameFolder") {
      setTreeNode(node);
      setFolderModalMode("rename");
      setFormData((prev) => ({ ...prev, FolderName: node.name || "" }));
      setFolderAddPopupShow(true);
    } else if (action === "deleteFolder") {
      const blocked = collectMilestonesBlockingFolderDelete(
        node.id,
        flatTreeData,
        templatePlaceholders,
        planMappingPreview,
      );
      if (blocked.length > 0) {
        const preview = blocked
          .slice(0, 4)
          .map((item) => `${item.code}${item.folderName ? ` (${item.folderName})` : ""}`)
          .join(", ");
        const suffix =
          blocked.length > 4 ? ` and ${blocked.length - 4} more` : "";
        showAlert({
          title: "Cannot delete folder",
          message: `This folder has milestone assignment(s): ${preview}${suffix}. Remove the milestone(s) first, then delete the folder.`,
          type: "warning",
        });
        return;
      }

      showConfirm({
        title: "Delete folder",
        message: `Delete folder "${node.name}" and all folders under it?`,
        confirmText: "Delete",
        confirmButtonClass: "btn-danger",
        icon: "fas fa-trash text-danger",
        onConfirm: () => {
          hideConfirm();
          const removedFolderIds = collectRemovedFolderIds(flatTreeData, node.id);
          setFlatTreeData((prev) => removeFolderAndDescendants(prev, node.id));
          setTemplatePlaceholders((prev) =>
            prev.filter((p) => !removedFolderIds.has(String(p.TemplateFolderId))),
          );
          setPlaceholdersTouched(true);
        },
        onCancel: () => hideConfirm(),
      });
    } else if (action === "editPlaceholder") {
      const folderId = node.parentId || node.placeholderMeta?.TemplateFolderId;
      const localKey = resolvePlaceholderLocalKey(node);
      if (folderId) setSelectedFolderId(folderId);
      if (localKey) setSelectedPlaceholderKey(localKey);
    } else if (action === "removePlaceholder") {
      removeTemplatePlaceholder(node);
    } else if (action === "removeAdHocMilestone") {
      const meta = node?.milestoneMeta;
      if (!meta?.isManualMilestone || !meta?.milestoneId) return;
      removeAdHocMilestoneFromFolder(meta.milestoneId, meta.folderId || node.parentId, {
        code: meta.code,
        name: meta.name,
      });
    }
  };

  const addItemsToTree = () => {
    const name = (formData.FolderName || "").trim();
    if (!name) {
      showAlert({
        title: "Error",
        message: "Please enter a folder name",
        type: "danger",
      });
      return;
    }

    if (folderModalMode === "rename" && treeNode) {
      const next = flatTreeData.map((row) =>
        String(row.FolderId) === String(treeNode.id)
          ? { ...row, FolderName: name }
          : row
      );
      setFlatTreeData(next);
    } else {
      let parentId = "";
      if (folderModalMode === "addSub" && treeNode) {
        parentId = treeNode.id;
      }
      const tempNode = {
        FolderId: utils.uuidv4(),
        FolderName: name,
        ParentFolderIdRef: parentId,
      };
      setFlatTreeData([...flatTreeData, tempNode]);
    }

    setFormData((prev) => ({
      ...prev,
      FolderName: "",
    }));

    setFolderAddPopupShow(false);
    setTreeNode(null);
    setFolderModalMode(null);
  };

  const assignMilestoneToFolder = async ({
    milestoneDetail,
    records,
    templateFolderId,
  }) => {
    const templateId = formData.TemplateId;
    if (!templateId) {
      throw new Error("Save the template before assigning milestones.");
    }

    const milestoneId = stripGuid(milestoneDetail?.MilestoneId);
    const usedIds = getAssignedMilestoneIds(templatePlaceholders);
    if (milestoneId && usedIds.has(milestoneId.toLowerCase())) {
      throw new Error("This milestone is already on the template.");
    }

    const folderSort = (templatePlaceholders || []).filter((p) =>
      placeholderMatchesFolder(p, templateFolderId),
    ).length;
    const folderName =
      flatTreeData.find(
        (f) => normFolderId(f.FolderId) === normFolderId(templateFolderId),
      )?.FolderName || "";

    const newRows = buildTemplatePlaceholdersFromMilestone({
      milestoneDetail,
      records,
      templateFolderId: stripGuid(templateFolderId),
      sortOrderStart: folderSort,
    }).map((row) => ({
      ...row,
      TemplateFolderId: stripGuid(templateFolderId),
      FolderName: folderName,
      _manualMilestone: true,
    }));

    if (!newRows.length) {
      throw new Error("No expected documents for this milestone.");
    }

    showLoader("Saving milestone assignment…");
    try {
      await persistManualTemplatePlaceholders(templateId, newRows, []);
      const reloaded = await reloadTemplateFoldersFromServer(templateId);
      if (reloaded) {
        captureModalBaseline({
          folders: reloaded.folders,
          placeholders: reloaded.placeholders,
        });
      }
      setPlaceholdersTouched(false);
      setSelectedFolderId(stripGuid(templateFolderId));
      setSelectedMilestoneKey(assignedMilestoneKey(milestoneId));
      return newRows;
    } finally {
      hideLoader();
    }
  };

  const removeAdHocMilestoneFromFolder = (milestoneId, folderId, label = {}) => {
    const mid = stripGuid(milestoneId);
    const fid = stripGuid(folderId);
    const templateId = formData.TemplateId;
    if (!mid || !fid || !templateId || !userData?.OrganizationId) return;

    const folderName =
      flatTreeData.find((f) => normFolderId(f.FolderId) === normFolderId(fid))?.FolderName ||
      "";
    const toRemove = (templatePlaceholders || []).filter((p) => {
      if (stripGuid(p.MilestoneId)?.toLowerCase() !== mid.toLowerCase()) return false;
      if (!placeholderMatchesFolder(p, fid, folderName)) return false;
      return inferManualMilestoneAssignment(p, planMappingPreview, flatTreeData);
    });

    if (!toRemove.length) return;

    const code = label.code || toRemove[0].MilestoneCode || "Milestone";
    const name = label.name || toRemove[0].MilestoneName || "";
    const displayLabel = name ? `${code} — ${name}` : code;
    const folderLabel = folderName || "this folder";

    showConfirm({
      title: "Remove ad-hoc milestone",
      message: `Remove "${displayLabel}" from folder "${folderLabel}"? The milestone stays in the library and can be assigned again from Folder assignments.`,
      confirmText: "Remove",
      confirmButtonClass: "btn-danger",
      icon: "fas fa-trash text-danger",
      onConfirm: async () => {
        hideConfirm();
        showLoader("Removing milestone…");
        try {
          const placeholderIds = [
            ...new Set(
              toRemove.map((p) => stripGuid(p.PlaceholderId)).filter(Boolean),
            ),
          ];
          for (const placeholderId of placeholderIds) {
            const response = await removeTemplatePlaceholderApi({
              organizationId: userData.OrganizationId,
              templateId,
              placeholderId,
              createdBy: userData.StaffId,
            });
            if (response?.success !== 1) {
              throw new Error(response?.message || "Failed to remove milestone assignment.");
            }
          }

          const removedPlaceholderIdSet = new Set(
            placeholderIds.map((id) => id.toLowerCase()),
          );
          const midLower = mid.toLowerCase();
          setTemplatePlaceholders((prev) =>
            prev.filter((p) => {
              const placeholderId = stripGuid(p.PlaceholderId)?.toLowerCase();
              if (placeholderId && removedPlaceholderIdSet.has(placeholderId)) {
                return false;
              }
              if (stripGuid(p.MilestoneId)?.toLowerCase() !== midLower) {
                return true;
              }
              return !(
                placeholderMatchesFolder(p, fid, folderName) &&
                inferManualMilestoneAssignment(p, planMappingPreview, flatTreeData)
              );
            }),
          );

          const reloaded = await reloadTemplateFoldersFromServer(templateId);
          if (reloaded) {
            captureModalBaseline({
              folders: reloaded.folders,
              placeholders: reloaded.placeholders,
            });
          }
          setPlaceholdersTouched(false);

          if (selectedMilestoneKey === assignedMilestoneKey(mid)) {
            setSelectedMilestoneKey(null);
          }
        } catch (err) {
          showAlert({
            title: "Error",
            message: err?.message || "Failed to remove milestone.",
            type: "danger",
          });
        } finally {
          hideLoader();
        }
      },
      onCancel: () => hideConfirm(),
    });
  };

  const resolvePlaceholderLocalKey = (nodeOrRow) => {
    if (!nodeOrRow) return "";
    return (
      nodeOrRow._localKey ||
      nodeOrRow.placeholderMeta?._localKey ||
      nodeOrRow.id ||
      (nodeOrRow.PlaceholderId ? `tpl-ph-${nodeOrRow.PlaceholderId}` : "")
    );
  };

  const removeTemplatePlaceholder = (nodeOrRow, { skipConfirm = false } = {}) => {
    const localKey = resolvePlaceholderLocalKey(nodeOrRow);
    const row =
      nodeOrRow?.placeholderMeta ||
      templatePlaceholders.find(
        (p) =>
          (localKey && p._localKey === localKey) ||
          placeholderSameIdentity(p, nodeOrRow),
      ) ||
      nodeOrRow;
    const label = row?.PlaceholderName || nodeOrRow?.name || "this document";

    const applyRemove = () => {
      const milestoneId = stripGuid(row?.MilestoneId)?.toLowerCase();
      const removeWholeMilestone = Boolean(milestoneId && row?._manualMilestone);

      setTemplatePlaceholders((prev) =>
        prev.filter((p) => {
          if (removeWholeMilestone) {
            return stripGuid(p.MilestoneId)?.toLowerCase() !== milestoneId;
          }
          return !placeholderSameIdentity(p, row);
        }),
      );
      setPlaceholdersTouched(true);
      if (
        selectedPlaceholderKey &&
        (selectedPlaceholderKey === localKey ||
          selectedPlaceholderKey === row?._localKey ||
          placeholderSameIdentity(
            { _localKey: selectedPlaceholderKey },
            row,
          ))
      ) {
        setSelectedPlaceholderKey(null);
      }
    };

    if (skipConfirm) {
      applyRemove();
      return;
    }

    showConfirm({
      title: "Remove document",
      message: `Remove "${label}" from this template?`,
      confirmText: "Remove",
      confirmButtonClass: "btn-danger",
      icon: "fas fa-trash text-danger",
      onConfirm: () => {
        hideConfirm();
        applyRemove();
      },
      onCancel: () => hideConfirm(),
    });
  };

  const buildPlaceholderPayload = (rows) =>
    (rows || []).map((r, i) => ({
      PlaceholderId: r.PlaceholderId || "",
      TemplateFolderId: r.TemplateFolderId,
      PlaceholderName: r.PlaceholderName,
      Description: r.Description || "",
      IsRequired: r.IsRequired !== false && r.IsRequired !== 0,
      SortOrder: r.SortOrder ?? i,
      DefaultDueDays:
        r.DefaultDueDays === "" || r.DefaultDueDays == null
          ? null
          : Number(r.DefaultDueDays),
      DefaultAssigneeRole: r.DefaultAssigneeRole || "",
      RequiresSignature:
        r.RequiresSignature === true ||
        r.RequiresSignature === 1 ||
        r.RequiresSignature === "1",
      SignerRoles: r.SignerRoles || "",
      AllowsNotApplicable:
        r.AllowsNotApplicable === true ||
        r.AllowsNotApplicable === 1 ||
        r.AllowsNotApplicable === "1",
      MilestoneId: stripGuid(r.MilestoneId) || "",
      _manualMilestone: Boolean(r._manualMilestone),
    }));

  const persistTemplatePlaceholders = async (templateId, rows) => {
    if (!templateId || !userData?.OrganizationId) return;
    const response = await saveTemplatePlaceholders({
      organizationId: userData.OrganizationId,
      templateId,
      createdBy: userData.StaffId,
      placeholders: buildPlaceholderPayload(rows),
    });
    if (response?.success !== 1) {
      throw new Error(response?.message || "Failed to save expected documents.");
    }
  };

  const persistManualTemplatePlaceholders = async (templateId, rows, removedIds = []) => {
    if (!templateId || !userData?.OrganizationId) return;

    for (const placeholderId of removedIds) {
      const response = await removeTemplatePlaceholderApi({
        organizationId: userData.OrganizationId,
        templateId,
        placeholderId,
        createdBy: userData.StaffId,
      });
      if (response?.success !== 1) {
        throw new Error(response?.message || "Failed to remove expected document.");
      }
    }

    if (!rows?.length) return;

    const payload = buildTemplatePlaceholderPayload(rows).filter(
      (row) => stripGuid(row.TemplateFolderId) && String(row.PlaceholderName || "").trim(),
    );
    if (!payload.length) {
      throw new Error(
        "Manual milestone assignment is missing folder information. Select a folder and try again.",
      );
    }

    const response = await mergeManualTemplatePlaceholders({
      organizationId: userData.OrganizationId,
      templateId,
      createdBy: userData.StaffId,
      placeholders: payload,
    });
    if (response?.success !== 1) {
      throw new Error(response?.message || "Failed to save manual assignments.");
    }
  };

  //section 6 save/update template
  const saveupdateTemplate = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    const templateName = String(formData.TemplateName || "").trim();
    if (!templateName) {
      showAlert({
        title: "Error",
        message: "Please fill the template name",
        type: "danger",
      });
      return;
    }
    if (templateName.length > TEMPLATE_NAME_MAX_LENGTH) {
      showAlert({
        title: "Error",
        message: `Template name cannot exceed ${TEMPLATE_NAME_MAX_LENGTH} characters.`,
        type: "danger",
      });
      return;
    }
    if (!formData.TemplateCategory?.label) {
      showAlert({
        title: "Error",
        message: "Please select a category",
        type: "danger",
      });
      return;
    }
    try {
      showLoader("Request processing...");

      const placeholdersToSave = dedupeTemplatePlaceholders(templatePlaceholders);
      const hasMilestonePlan = Boolean(selectedMilestonePlan?.value);
      const shouldSaveAllPlaceholders =
        canEditPlaceholders && placeholdersTouched && !hasMilestonePlan;
      const shouldReapplyMilestonePlan =
        hasMilestonePlan && (milestonePlanTouched || !formData.TemplateId);

      const response = await api.post(API_ENDPOINTS.ISF.GET_TEMPLATE_BY_ID, {
        TemplateId: formData.TemplateId,
        TemplateName: templateName,
        OrganizationId: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        ActionType: formData.TemplateId === "" ? "Save" : "Update",
        TemplateCategory: formData.TemplateCategory?.label ?? "",
        MilestonePlanId: selectedMilestonePlan?.value || "",
        ApplyMilestonePlanOnSave: shouldReapplyMilestonePlan,
        Folders: flatTreeData.map((item) => ({
          FolderId: item.FolderId,
          FolderName: item.FolderName,
          ParentFolderId: item.ParentFolderIdRef,
        })),
      });
      if (response.success === 1) {
        let templateIdToSave =
          formData.TemplateId ||
          response.data?.SuccessStory?.[0]?.TemplateID ||
          response.data?.SuccessStory?.[0]?.TemplateId ||
          "";

        if (!templateIdToSave && formData.TemplateName) {
          const listResponse = await api.post(API_ENDPOINTS.ISF.TEMPLATE_LIST, {
            TemplateId: "",
            TemplateName: "",
            OrganizationId: userData.OrganizationId,
            CreatedBy: userData.StaffId,
            ActionType: "GetTemplateListData",
            TemplateCategory: null,
            Folders: null,
          });
          if (listResponse.success === 1) {
            const match = (listResponse.data || []).find(
              (t) =>
                String(t.TemplateName || "").trim().toLowerCase() ===
                String(formData.TemplateName).trim().toLowerCase(),
            );
            templateIdToSave = match?.TemplateID || match?.TemplateId || "";
          }
        }

        if (templateIdToSave && shouldSaveAllPlaceholders) {
          try {
            await persistTemplatePlaceholders(templateIdToSave, placeholdersToSave);
          } catch (phError) {
            resetForm();
            getAllTemplates();
            setTemplatePopupShow(false);
            showAlert({
              title: "Warning",
              message:
                phError?.message ||
                "Template saved but expected documents could not be saved. Please try again or contact your system administrator.",
              type: "warning",
            });
            return;
          }
        }

        const wasNewTemplate = !formData.TemplateId;

        if (templateIdToSave && (shouldSaveAllPlaceholders || hasMilestonePlan)) {
          const reloaded = await reloadTemplateFoldersFromServer(templateIdToSave);
          if (reloaded) {
            captureModalBaseline({
              folders: reloaded.folders,
              placeholders: reloaded.placeholders,
            });
          }
        }

        if (wasNewTemplate && templateIdToSave) {
          setFormData((prev) => ({ ...prev, TemplateId: templateIdToSave }));
          setPlaceholdersTouched(false);
          setMilestonePlanTouched(false);
          getAllTemplates();
          showAlert({
            title: "Success",
            message: "Template created. You can now assign milestones to folders.",
            type: "success",
          });
        } else {
          const successMessage =
            response?.message ||
            response.data?.SuccessStory?.[0]?.MSG ||
            "Template saved successfully.";

          resetForm();
          getAllTemplates();
          setTemplatePopupShow(false);
          showAlert({
            title: response?.message?.includes("could not") ? "Warning" : "Success",
            message: successMessage,
            type: response?.message?.includes("could not") ? "warning" : "success",
          });
        }
      } else {
        showAlert({
          title: "Error",
          message:
            response?.message ||
            response?.data?.SuccessStory?.[0]?.MSG ||
            "Failed to save template.",
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save template.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const selectedFolderName =
    flatTreeData.find(
      (f) => normFolderId(f.FolderId) === normFolderId(selectedFolderId),
    )?.FolderName || "";

  const createdByFilterOptions = useMemo(() => {
    const names = new Set();
    (templates || []).forEach((row) => {
      const name = getTemplateCreatedByLabel(row);
      if (name && name !== "—") names.add(name);
    });
    return [...names]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  }, [templates]);

  const filteredTemplates = useMemo(
    () =>
      (templates || []).filter((row) => {
        if (lock && normalizeFolderCategory(row.TemplateCategory) !== lock) {
          return false;
        }
        if (tableFilters.createdBy?.length) {
          const createdBy = getTemplateCreatedByLabel(row);
          if (!tableFilters.createdBy.includes(createdBy)) return false;
        }
        if (!matchesTemplateDateRange(row?.SystemDateTime, tableFilters.createdDateRange)) {
          return false;
        }
        return true;
      }),
    [templates, tableFilters, lock],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "TemplateName") {
        return (
          <button
            type="button"
            className="report-table__cell-link isf-template-name-link"
            title="Edit template"
            onClick={() => handleEdit(row)}
          >
            {row.TemplateName || "—"}
          </button>
        );
      }
      if (field === ISF_REPORT_ACTIONS_COLUMN) {
        return (
          <button
            type="button"
            className="isf-template-delete-icon"
            title="Delete template"
            aria-label={`Delete ${row.TemplateName || "template"}`}
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(row);
            }}
          >
            <i className="fas fa-trash-alt" aria-hidden="true" />
          </button>
        );
      }
      if (field === "SystemDateTime") {
        return utils.formatDateTimeDisplay(row.SystemDateTime) || row.SystemDateTime || "—";
      }
      if (field === "TemplateCategory") {
        return normalizeFolderCategory(row.TemplateCategory) || "—";
      }
      if (field === "MilestonePlanName" || field === "CreatedByName") {
        const value = String(row[field] ?? "").trim();
        return value || "—";
      }
      if (field === "FolderCount") {
        const count = Number(row[field]);
        return Number.isFinite(count) ? String(count) : "0";
      }
      return String(row[field] ?? "—");
    },
    [handleEdit, handleDelete],
  );

  const renderColumnSearch = useCallback((field, options) => {
    if (
      field !== "TemplateCategory" &&
      field !== "CreatedByName" &&
      field !== "SystemDateTime"
    ) {
      return undefined;
    }
    if (options?.checkOnly) return true;
    if (field === "CreatedByName") {
      return (
        <BadgeMultiSelect
          options={createdByFilterOptions}
          value={tableFilters.createdBy}
          onChange={(values) =>
            setTableFilters((prev) => ({ ...prev, createdBy: values }))
          }
          emptyLabel="All creators"
          addLabel="+"
          popoverTitle="Filter by created by"
          popoverSubtitle="Select one or more creators"
          showSearch
          compactSummary
          disabled={options?.disabled}
        />
      );
    }
    if (field === "SystemDateTime") {
      return (
        <DateRangeFilter
          value={tableFilters.createdDateRange}
          onChange={(range) =>
            setTableFilters((prev) => ({
              ...prev,
              createdDateRange: range || { ...TEMPLATE_EMPTY_DATE_RANGE },
            }))
          }
          placeholder="Creation date range"
          presetOptions={TEMPLATE_DATE_PRESETS}
          compact
          disabled={options?.disabled}
        />
      );
    }
    return (
      <select
        className="report-table__column-search"
        value={options?.value || ""}
        disabled={options?.disabled || Boolean(lock)}
        aria-label="Filter by category"
        onChange={(event) => options?.onChange?.(event.target.value)}
      >
        {!lock ? <option value="">All</option> : null}
        {(lock
          ? FOLDER_LIBRARY_CATEGORIES.filter((item) => item.value === lock)
          : FOLDER_LIBRARY_CATEGORIES
        ).map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    );
  }, [createdByFilterOptions, tableFilters, lock]);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (
      field === ISF_REPORT_ACTIONS_COLUMN ||
      field === "CreatedByName" ||
      field === "SystemDateTime"
    ) {
      return true;
    }
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (field === "TemplateCategory") {
      return normalizeFolderCategory(row.TemplateCategory).toLowerCase() === term;
    }
    return matchTextColumnSearch(row, field, searchValue, [
      ISF_REPORT_ACTIONS_COLUMN,
      "SystemDateTime",
      "TemplateCategory",
      "CreatedByName",
    ]);
  }, []);

  const getFieldLabel = useCallback(
    (field) =>
      TEMPLATE_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const layoutStorageKey = userData?.OrganizationId
    ? `isf-template-report-v5-${userData.OrganizationId}`
    : "";
  const CrumbBar = lock === "ePSF" ? EpsfPageBreadcrumb : PageBreadcrumb;

  return (
    <div className="isf-report-page">
      <CrumbBar
        items={
          lock
            ? [
                { label: "ePSF", path: "/epsf/imp/inventory" },
                { label: "Templates" },
              ]
            : [
                { label: "Home", path: "/notification-center" },
                { label: "Template Manager" },
              ]
        }
        actions={[
          { icon: "fas fa-refresh", label: "Refresh", onClick: handleRefresh },
          {
            icon: "fas fa-plus",
            label: "Add Template",
            onClick: () => handleEdit(null),
          },
        ]}
      />

      <div className="isf-report-table-wrap">
        <ReportTable
          rows={filteredTemplates}
          fieldOptions={TEMPLATE_FIELD_OPTIONS}
          defaultColumns={DEFAULT_TEMPLATE_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          persistLayoutKey={layoutStorageKey}
          pinnedRightColumns={ISF_REPORT_PINNED_COLUMNS}
          hideAddColumnFields={[ISF_REPORT_ACTIONS_COLUMN]}
          hideColumnHeaderActionsFields={[ISF_REPORT_ACTIONS_COLUMN]}
          loading={listLoading}
        />
      </div>

      {isTemplatePopupShow && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl tpl-modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Template Add/Edit</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={requestCloseTemplateModal}
                />
              </div>

              <div className="modal-body tpl-modal-body">
                <div className="tpl-modal__columns">
                  <div className="tpl-editor__panel tpl-editor__panel--setup">
                    <div className="tpl-editor__panel-head">
                      <h6>Template setup</h6>
                      <p>Category, name, and milestone plan.</p>
                    </div>
                    <div className="tpl-editor__panel-body tpl-modal__setup">
                      <div className="tpl-modal__field">
                        <label>
                          Category <span className="mandatory">*</span>
                        </label>
                        <Dropdown
                          options={templateCategory}
                          isMulti={false}
                          selected={formData.TemplateCategory}
                          onSelect={handleCategory}
                          disabled={formData.TemplateId !== "" || Boolean(lock)}
                        />
                      </div>
                      <div className="tpl-modal__field">
                        <label>
                          Template Name <span className="mandatory">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleFormInput}
                          name="TemplateName"
                          value={formData.TemplateName || ""}
                          maxLength={TEMPLATE_NAME_MAX_LENGTH}
                        />
                      </div>
                      <div className="tpl-modal__field">
                        <label>Milestone Plan</label>
                        <Dropdown
                          options={milestonePlanOptions}
                          isMulti={false}
                          selected={selectedMilestonePlan}
                          onSelect={(plan) => {
                            setSelectedMilestonePlan(plan || null);
                            setMilestonePlanTouched(true);
                            setPlaceholdersTouched(false);
                            setSelectedMilestoneKey(null);
                          }}
                          placeholder="Select a published milestone plan"
                          disabled={!canSelectMilestonePlan || Boolean(formData.TemplateId)}
                        />
                        <small className="text-muted">
                          {formData.TemplateId
                            ? "Milestone plan is locked after the template is created."
                            : "Optional. Only published plans appear here. Plan placeholders apply when the template is saved."}
                        </small>
                        {!canSelectMilestonePlan && !formData.TemplateId ? (
                          <small className="text-warning d-block mt-1">
                            You need Template Manager permission to assign a milestone plan.
                          </small>
                        ) : null}
                        {milestonePlanLoadError ? (
                          <small className="text-danger d-block mt-1">
                            {milestonePlanLoadError}
                          </small>
                        ) : null}
                        {!milestonePlanLoadError &&
                        canSelectMilestonePlan &&
                        !formData.TemplateId &&
                        !milestonePlanOptions.length ? (
                          <small className="text-warning d-block mt-1">
                            No published milestone plans found. Create and publish a plan under
                            eISF &gt; Milestone Plan first.
                          </small>
                        ) : null}
                        {selectedMilestonePlan?.value && !planMappingPreviewLoading ? (
                          <small className="text-muted d-block mt-1">
                            {planMappingStats.total} milestone(s) · {planMappingStats.placeholders}{" "}
                            document(s)
                            {planMappingStats.willCreate > 0
                              ? ` · ${planMappingStats.willCreate} new folder(s)`
                              : ""}
                            {planMappingStats.missing > 0
                              ? ` · ${planMappingStats.missing} skipped (no default folder)`
                              : ""}
                          </small>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="tpl-editor__panel tpl-editor__panel--tree">
                    <div className="tpl-editor__panel-head">
                      <h6>Folder structure</h6>
                      <p>
                        Folders load from the folder library for the selected category (each time
                        you add a template or change category). Milestone folders expand when a
                        plan is selected.
                      </p>
                    </div>
                    <div className="tpl-editor__panel-body">
                      <TreeView
                        data={treeData}
                        selectedNodeId={selectedMilestoneKey || selectedFolderId}
                        forceExpandedIds={expandedFolderIds}
                        onAction={(node, action) => handleTreeAction(node, action)}
                        onSelect={(node) => {
                          if (node?.type === "milestone" && node?.isPlanMilestone) {
                            setSelectedMilestoneKey(node.id);
                            if (node.parentId) setSelectedFolderId(node.parentId);
                            setSelectedPlaceholderKey(null);
                          } else if (
                            node?.type === "milestone" &&
                            (node?.isAssignedMilestone || node?.isManualMilestone)
                          ) {
                            setSelectedMilestoneKey(node.id);
                            if (node.parentId) setSelectedFolderId(node.parentId);
                            setSelectedPlaceholderKey(null);
                          } else if (node?.type === "folder" && node?.id) {
                            setSelectedFolderId(node.id);
                            setSelectedMilestoneKey(null);
                            setSelectedPlaceholderKey(null);
                          } else if (
                            node?.type === "placeholder" &&
                            node?.isTemplatePlaceholder
                          ) {
                            const folderId =
                              node.parentId || node.placeholderMeta?.TemplateFolderId;
                            if (folderId) setSelectedFolderId(folderId);
                            setSelectedMilestoneKey(null);
                          }
                        }}
                        folderMenuVariant="template"
                        canMove={false}
                        canEditPlaceholders={canEditPlaceholders}
                      />
                    </div>
                  </div>

                  <div className="tpl-editor__panel tpl-editor__panel--detail">
                    <div className="tpl-editor__panel-head">
                      <h6>Details</h6>
                      <p>Selected folder or milestone.</p>
                    </div>
                    <div className="tpl-editor__panel-body">
                      <TemplateSelectionDetail
                        selectedFolderId={selectedFolderId}
                        selectedMilestoneKey={selectedMilestoneKey}
                        flatTreeData={flatTreeData}
                        planMappingPreview={planMappingPreview}
                        templatePlaceholders={templatePlaceholders}
                        selectedMilestonePlan={selectedMilestonePlan}
                        planMappingPreviewLoading={planMappingPreviewLoading}
                        roleOptions={roleOptions}
                        milestoneOptions={milestoneLibraryOptions}
                        milestoneLookupMap={milestoneLookupMap}
                        milestoneLibraryLoading={milestoneLibraryLoading}
                        canEditPlaceholders={canEditPlaceholders}
                        templateId={formData.TemplateId}
                        userData={userData}
                        onAssignMilestone={assignMilestoneToFolder}
                        onRemoveAdHocMilestone={removeAdHocMilestoneFromFolder}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-primary me-2"
                  onClick={requestCloseTemplateModal}
                >
                  <i className="fas fa-close me-2" />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    saveupdateTemplate();
                  }}
                >
                  {formData.TemplateId === "" ? (
                    <>
                      <i className="fas fa-plus me-2" />
                      Add
                    </>
                  ) : (
                    <>
                      <i className="fas fa-edit me-2" />
                      Update
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isFolderAddPopupShow && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {folderModalMode === "rename"
                    ? "Rename folder"
                    : folderModalMode === "addSub"
                      ? "Add sub folder"
                      : "Add root folder"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setFolderAddPopupShow(false);
                    setTreeNode(null);
                    setFolderModalMode(null);
                  }}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      Folder Name <span className="mandatory">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      onChange={handleFormInput}
                      name="FolderName"
                      value={formData.FolderName || ""}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="row">
                  <div className="col-md-12">
                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={() => {
                        setFolderAddPopupShow(false);
                        setTreeNode(null);
                        setFolderModalMode(null);
                      }}
                    >
                      <i className="fas fa-close me-2"></i>
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        addItemsToTree();
                      }}
                    >
                      {folderModalMode === "rename" ? (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus me-2"></i>
                          Add
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add all the modals and toasts */}
      <ConfirmModal
        isOpen={modalState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        {...modalState}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ISFTemplate;
